import { Modal, ModalProps } from './Modal';
import styles from './InstagramPostsModal.module.scss';
import { Button } from '../button';
import { Dropdown } from '../dropdown';
import { app } from 'application';
import { type DropdownOption } from '../dropdown/Dropdown';
import { observer } from 'mobx-react-lite';
import { InstagramPost } from 'domains/instagramPost';
import { useEffect, useState, useRef } from 'react';
import { Creative } from 'domains/creative';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { InstagramMedia, MediaUpdateDTO } from 'infrastructure/dtos/instagramPost';
import { Media } from 'domains/media';

function InstagramPostsModal(props: Omit<ModalProps, 'children'>): React.ReactElement {
  const { isOpen, onClose } = props;
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [modalContentWidth, setModalContentWidth] = useState<number>(0);

  const [selectedDropdownCategory, setSelectedDropdownCategory] = useState<DropdownOption | null>(
    null
  );

  const posts = app().instagramStore.instagramPosts || [];

  const [selectedPost, setSelectedPost] = useState<InstagramPost | undefined>(undefined);
  const [selectedCreative, setSelectedCreative] = useState<Creative | undefined>(undefined);

  const [isUploadingMedia, setIsUploadingMedia] = useState(false);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mediaAspectRatios, setMediaAspectRatios] = useState<{
    [key: string]: number;
  }>({});

  const [categoriesDropdownOptions, setCategoriesDropdownOptions] = useState<DropdownOption[]>([]);

  useEffect((): void => {
    if (!selectedPost && isOpen) {
      const _creatives = app().creativesStore.creatives?.items || [];
      const _newPosts = app().instagramStore.instagramPosts || [];
      const _categories = app().categoriesStore.categories || [];

      const { category: newPostCategory } = _newPosts[0];

      if (newPostCategory) {
        setCategoriesDropdownOptions(
          _categories
            .find(category => category.id === newPostCategory.id)
            ?.children?.map(cat => ({
              label: cat.name,
              value: cat.id,
            })) || []
        );
      }

      setSelectedPost(_newPosts[0]);
      setSelectedCreative(
        _creatives.find(creative => creative.id === _newPosts[0].userId.toString())
      );
    }
  }, [selectedPost, isOpen]);

  useEffect((): void => {
    // Reset current image index when post changes
    setCurrentImageIndex(0);
  }, [selectedPost]);

  useEffect(() => {
    const updateModalContentWidth = () => {
      if (modalContentRef.current) {
        setModalContentWidth(modalContentRef.current.offsetWidth);
      }
    };

    // Initial measurement
    updateModalContentWidth();

    // Update on window resize
    window.addEventListener('resize', updateModalContentWidth);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateModalContentWidth);
    };
  }, [isOpen]); // Re-run when modal opens/closes

  const handleNextImage: () => void = () => {
    if (!selectedPost?.media.children.length) return;
    setCurrentImageIndex(prev => (prev === selectedPost.media.children.length - 1 ? 0 : prev + 1));
  };

  const handlePreviousImage: () => void = () => {
    if (!selectedPost?.media.children.length) return;
    setCurrentImageIndex(prev => (prev === 0 ? selectedPost.media.children.length - 1 : prev - 1));
  };

  const handleDotClick: (index: number) => void = (index: number) => {
    setCurrentImageIndex(index);
  };

  const handleCloseModal: () => void = () => {
    setSelectedPost(undefined);
    onClose();
  };

  const handleChangeCategory: (value: string) => void = (value: string) => {
    setSelectedDropdownCategory(categoriesDropdownOptions.find(cat => cat.value === value) || null);
  };

  async function uploadMediaToCloudinary(media: Media): Promise<InstagramMedia> {
    const instagramMedia: InstagramMedia = {
      resolutions: [],
    };

    if (media.isPlayableVideo && media.mediaUrl) {
      const cloudinaryResponse = await app().mediaService.downloadAndUploadToCloudinary(
        media.mediaUrl,
        'instagram_videos'
      );

      instagramMedia.resolutions.push({
        mime_type: 'video/mp4',
        name: cloudinaryResponse.assetId,
        url: cloudinaryResponse.secureUrl,
        primary: false,
        aspect_ratio: getAspectRatio(media.displayUrl),
      });
    } else if (media.displayUrl) {
      const cloudinaryResponse = await app().mediaService.downloadAndUploadToCloudinary(
        media.displayUrl,
        'instagram_pictures'
      );

      instagramMedia.resolutions.push({
        mime_type: 'image/jpeg',
        name: cloudinaryResponse.assetId,
        url: cloudinaryResponse.secureUrl,
        primary: false,
        aspect_ratio: getAspectRatio(media.displayUrl),
      });
    }

    return instagramMedia;
  }

  const handleApprovePost: () => void = async () => {
    if (!selectedPost || !selectedDropdownCategory) return;

    const {
      media: { children },
    } = selectedPost;

    const mediaUpdateDTO: MediaUpdateDTO = {
      instagram_post_id: selectedPost.instagramPostId,
      medias: [],
    };

    setIsUploadingMedia(true);

    if (children.length === 0) {
      const instagramMedia = await uploadMediaToCloudinary(selectedPost.media);
      mediaUpdateDTO.medias.push(instagramMedia);
    } else {
      mediaUpdateDTO.medias = await Promise.all(
        children.map(child => uploadMediaToCloudinary(child))
      );
    }

    try {
      await app().instagramStore.updateMedia(mediaUpdateDTO);
      await app().instagramStore.approvePost({
        posts: [
          {
            id: selectedPost?.id.toString() || '',
            category_id: selectedDropdownCategory.value,
            aspect_ratio: getAspectRatio(selectedPost?.media.displayUrl || ''),
          },
        ],
      });
      const _posts = app().instagramStore.instagramPosts || [];
      if (_posts.length > 0) {
        setSelectedPost(_posts[0]);
      } else {
        onClose();
      }
      toast.success('Post approved');
    } catch (error) {
      toast.error('Failed to approve post');
    } finally {
      setIsUploadingMedia(false);
    }
  };

  const handleRejectPost: () => void = async () => {
    if (!selectedPost) return;
    try {
      await app().instagramStore.rejectPost({
        posts: [
          {
            id: selectedPost?.id.toString() || '',
            reason: 'not relevant',
          },
        ],
      });
      const _posts = app().instagramStore.instagramPosts || [];
      if (_posts.length > 0) {
        setSelectedPost(_posts[0]);
      } else {
        onClose();
      }
      toast.success('Post rejected');
    } catch (error) {
      toast.error('Failed to reject post');
    }
  };

  const handleSelectPost: (post: InstagramPost) => void = (post: InstagramPost) => {
    const _categories = app().categoriesStore.categories || [];

    const { category: postCategory } = post;

    if (postCategory) {
      setCategoriesDropdownOptions(
        _categories
          .find(category => category.id === postCategory.id)
          ?.children?.map(cat => ({
            label: cat.name,
            value: cat.id,
          })) || []
      );
    }

    setSelectedPost(post);
    setSelectedCreative(
      app().creativesStore.creatives?.items.find(creative => creative.id === post.userId.toString())
    );
  };

  const handleImageLoad = (url: string, event: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;

    const aspectRatio = parseFloat((naturalWidth / naturalHeight).toFixed(4));

    setMediaAspectRatios(prev => ({
      ...prev,
      [url]: aspectRatio,
    }));
  };

  const handleVideoLoad = (url: string, event: React.SyntheticEvent<HTMLVideoElement>) => {
    const { videoWidth, videoHeight } = event.currentTarget;

    const aspectRatio = parseFloat((videoWidth / videoHeight).toFixed(4));

    setMediaAspectRatios(prev => ({
      ...prev,
      [url]: aspectRatio,
    }));
  };

  function getAspectRatio(url: string): number {
    return mediaAspectRatios[url] || 1;
  }

  return (
    <Modal
      contentClassName={styles.modalContentContainer}
      isOpen={isOpen}
      onClose={handleCloseModal}
    >
      <div className={styles.modalContent} ref={modalContentRef}>
        <div className={styles.modalPost}>
          <div className={styles.modalPostHeader}>
            {selectedCreative?.profileImage ? (
              <img
                src={selectedCreative?.profileImage}
                alt="creative"
                className={styles.modalPostCreativeImage}
              />
            ) : (
              <div className={styles.modalPostCreativeImagePlaceholder} />
            )}

            <div className={styles.modalPostHeaderInfo}>
              <span className={styles.modalPostHeaderInfoName}>{selectedCreative?.username}</span>
              <span className={styles.modalPostHeaderInfoUsername}>
                {selectedCreative?.username}
              </span>
            </div>
          </div>

          <div
            className={styles.modalPostMedias}
            style={{
              minHeight: modalContentWidth / getAspectRatio(selectedPost?.media.displayUrl || ''),
              width: modalContentWidth,
            }}
          >
            {selectedPost?.media.children.length ? (
              <div className={styles.modalPostCarousel}>
                <div className={styles.modalPostCarouselPagination}>
                  <button
                    className={styles.modalPostCarouselPaginationButton}
                    onClick={handlePreviousImage}
                  >
                    <span
                      className={classNames(
                        styles.modalPostCarouselPaginationButtonIcon,
                        styles.modalPostCarouselPaginationButtonIconLeft
                      )}
                    />
                  </button>
                  <button
                    className={styles.modalPostCarouselPaginationButton}
                    onClick={handleNextImage}
                  >
                    <span
                      className={classNames(
                        styles.modalPostCarouselPaginationButtonIcon,
                        styles.modalPostCarouselPaginationButtonIconRight
                      )}
                    />
                  </button>
                </div>
                {selectedPost?.media.children.map((child, index) =>
                  child.isPlayableVideo && child.mediaUrl ? (
                    <video
                      key={child.displayUrl}
                      src={child.mediaUrl}
                      poster={child.thumbnailUrl}
                      className={classNames(
                        styles.modalPostCarouselItem,
                        index === currentImageIndex && styles.active
                      )}
                      controls
                      style={{ zIndex: index === currentImageIndex ? 2 : 1 }}
                      onLoadedMetadata={e => handleVideoLoad(child.displayUrl, e)}
                    />
                  ) : (
                    <img
                      key={child.displayUrl}
                      src={child.displayUrl}
                      className={classNames(
                        styles.modalPostCarouselItem,
                        index === currentImageIndex && styles.active
                      )}
                      alt="post"
                      style={{
                        zIndex: index === currentImageIndex ? 2 : 1,
                        aspectRatio: getAspectRatio(child.displayUrl),
                        width: '100%',
                        height: undefined,
                      }}
                      onLoad={e => handleImageLoad(child.displayUrl, e)}
                    />
                  )
                )}
                <div className={styles.modalPostCarouselDots}>
                  {selectedPost?.media.children.map((_, index) => (
                    <div
                      key={index}
                      className={classNames(
                        styles.modalPostCarouselDot,
                        index === currentImageIndex && styles.active
                      )}
                      onClick={() => handleDotClick(index)}
                    />
                  ))}
                </div>
              </div>
            ) : selectedPost?.media.isPlayableVideo && selectedPost.media.mediaUrl ? (
              <video
                src={selectedPost.media.mediaUrl}
                poster={selectedPost.media.thumbnailUrl}
                className={styles.modalPostMedia}
                controls
              />
            ) : (
              <img
                src={selectedPost?.media.displayUrl}
                alt="post"
                className={styles.modalPostMedia}
              />
            )}
          </div>

          <span className={styles.modalPostCaption}>{selectedPost?.caption}</span>
        </div>

        {posts.length > 0 && (
          <div className={styles.modalPostPagination}>
            {posts.map(post => (
              <div
                onClick={() => handleSelectPost(post)}
                key={post.id}
                className={styles.modalPostPaginationItem}
              >
                {post.media.isPlayableVideo && post.media.mediaUrl ? (
                  <video
                    src={post.media.mediaUrl}
                    poster={post.media.thumbnailUrl}
                    className={styles.modalPostPaginationItemImage}
                    onLoadedMetadata={e => handleVideoLoad(post.media.displayUrl, e)}
                  />
                ) : (
                  <img
                    src={post.media.displayUrl}
                    alt="post"
                    className={styles.modalPostPaginationItemImage}
                    onLoad={e => handleImageLoad(post.media.displayUrl, e)}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className={styles.modalForm}>
          <Dropdown
            disabled={!selectedPost?.category}
            placeholder="Select Sub Category"
            options={categoriesDropdownOptions}
            value={selectedDropdownCategory?.value || ''}
            onChange={handleChangeCategory}
          />

          <div className={styles.modalFormButtons}>
            <Button
              isLoading={app().instagramStore.isRejecting}
              onClick={handleRejectPost}
              theme="Accent"
            >
              Reject
            </Button>
            <Button
              isLoading={app().instagramStore.isApproving || isUploadingMedia}
              onClick={handleApprovePost}
              theme="Primary"
            >
              Approve
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const InstagramPostsModalWithObserver = observer(InstagramPostsModal);

export { InstagramPostsModalWithObserver as InstagramPostsModal };
